import React, { useState, useEffect, useCallback } from 'react';

const PRIORITIES = ['high', 'med', 'low'];
const CATEGORIES = ['business', 'personal'];

const PRIORITY_COLORS = { high: '#ef4444', med: '#f59e0b', low: '#22c55e' };
const PRIORITY_BG = { high: 'rgba(239,68,68,0.1)', med: 'rgba(245,158,11,0.1)', low: 'rgba(34,197,94,0.1)' };

const s = {
  heading: { fontSize: '1.3rem', fontWeight: '700', color: '#e0e0e0', marginBottom: '1.25rem' },
  addForm: {
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '10px',
    padding: '1.25rem', marginBottom: '1.5rem',
  },
  formRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  input: {
    flex: '1', minWidth: '180px', padding: '0.6rem 0.85rem',
    background: '#0f0f23', border: '1px solid #2a2a4a', borderRadius: '7px',
    color: '#e0e0e0', fontSize: '0.95rem', outline: 'none',
  },
  select: {
    padding: '0.6rem 0.75rem', background: '#0f0f23', border: '1px solid #2a2a4a',
    borderRadius: '7px', color: '#e0e0e0', fontSize: '0.9rem', cursor: 'pointer',
  },
  addBtn: {
    padding: '0.6rem 1.2rem', background: '#4f46e5', border: 'none',
    borderRadius: '7px', color: '#fff', fontWeight: '600', cursor: 'pointer',
    fontSize: '0.9rem',
  },
  taskList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  taskCard: (done) => ({
    background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '10px',
    padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
    opacity: done ? 0.55 : 1,
  }),
  checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#4f46e5', flexShrink: 0 },
  taskText: (done) => ({
    flex: 1, color: '#e0e0e0', textDecoration: done ? 'line-through' : 'none',
    fontSize: '0.95rem',
  }),
  badge: (priority) => ({
    padding: '0.2rem 0.55rem', borderRadius: '4px', fontSize: '0.72rem',
    fontWeight: '700', color: PRIORITY_COLORS[priority], background: PRIORITY_BG[priority],
    textTransform: 'uppercase', flexShrink: 0,
  }),
  catBadge: {
    padding: '0.2rem 0.55rem', borderRadius: '4px', fontSize: '0.72rem',
    background: 'rgba(79,70,229,0.15)', color: '#818cf8', flexShrink: 0,
  },
  iconBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: '#555', padding: '0.25rem', fontSize: '1rem', flexShrink: 0,
  },
  editInput: {
    flex: 1, padding: '0.35rem 0.6rem', background: '#0f0f23',
    border: '1px solid #4f46e5', borderRadius: '6px', color: '#e0e0e0',
    fontSize: '0.95rem', outline: 'none',
  },
  empty: { color: '#555', textAlign: 'center', padding: '2rem', fontSize: '0.95rem' },
  error: { color: '#f87171', fontSize: '0.85rem', marginTop: '0.5rem' },
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [newText, setNewText] = useState('');
  const [newPriority, setNewPriority] = useState('med');
  const [newCategory, setNewCategory] = useState('personal');
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editPriority, setEditPriority] = useState('med');
  const [editCategory, setEditCategory] = useState('personal');

  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch('/api/tasks', { credentials: 'same-origin' });
      if (res.ok) setTasks(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  async function addTask(e) {
    e.preventDefault();
    setError('');
    if (!newText.trim()) return;
    const res = await fetch('/api/tasks', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: newText, priority: newPriority, category: newCategory }),
    });
    if (res.ok) {
      const task = await res.json();
      setTasks(prev => [task, ...prev]);
      setNewText('');
    } else {
      const d = await res.json();
      setError(d.error || 'Failed to add task');
    }
  }

  async function toggleDone(task) {
    const res = await fetch(`/api/tasks/${task.id}`, {
      method: 'PUT', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ done: !task.done }),
    });
    if (res.ok) {
      const updated = await res.json();
      setTasks(prev => prev.map(t => t.id === task.id ? updated : t));
    }
  }

  async function deleteTask(id) {
    const res = await fetch(`/api/tasks/${id}`, {
      method: 'DELETE', credentials: 'same-origin',
    });
    if (res.ok) setTasks(prev => prev.filter(t => t.id !== id));
  }

  function startEdit(task) {
    setEditId(task.id);
    setEditText(task.text);
    setEditPriority(task.priority);
    setEditCategory(task.category);
  }

  async function saveEdit(id) {
    const res = await fetch(`/api/tasks/${id}`, {
      method: 'PUT', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: editText, priority: editPriority, category: editCategory }),
    });
    if (res.ok) {
      const updated = await res.json();
      setTasks(prev => prev.map(t => t.id === id ? updated : t));
      setEditId(null);
    }
  }

  return (
    <div>
      <div style={s.heading}>Tasks</div>
      <form style={s.addForm} onSubmit={addTask}>
        <div style={s.formRow}>
          <input
            style={s.input} value={newText} placeholder="New task..."
            onChange={e => setNewText(e.target.value)} required
          />
          <select style={s.select} value={newPriority} onChange={e => setNewPriority(e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
          <select style={s.select} value={newCategory} onChange={e => setNewCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <button style={s.addBtn} type="submit">Add Task</button>
        </div>
        {error && <div style={s.error}>{error}</div>}
      </form>

      <div style={s.taskList}>
        {tasks.length === 0 && <div style={s.empty}>No tasks yet. Add one above.</div>}
        {tasks.map(task => (
          <div key={task.id} style={s.taskCard(!!task.done)}>
            <input type="checkbox" style={s.checkbox} checked={!!task.done} onChange={() => toggleDone(task)} />
            {editId === task.id ? (
              <>
                <input style={s.editInput} value={editText} onChange={e => setEditText(e.target.value)} autoFocus />
                <select style={s.select} value={editPriority} onChange={e => setEditPriority(e.target.value)}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select style={s.select} value={editCategory} onChange={e => setEditCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button style={{ ...s.iconBtn, color: '#22c55e' }} onClick={() => saveEdit(task.id)} title="Save">✓</button>
                <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => setEditId(null)} title="Cancel">✕</button>
              </>
            ) : (
              <>
                <span style={s.taskText(!!task.done)}>{task.text}</span>
                <span style={s.badge(task.priority)}>{task.priority}</span>
                <span style={s.catBadge}>{task.category}</span>
                <button style={s.iconBtn} onClick={() => startEdit(task)} title="Edit">✎</button>
                <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => deleteTask(task.id)} title="Delete">🗑</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
